import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card } from './ui/Card';
import { Alert } from './ui/Alert';
import { Switch } from './ui/Switch';
import { apiFetch } from '../lib/api';
import { useAuth } from '../lib/auth';
import type { TrackingSettings } from '../types/api';

/**
 * The switch for browse tracking of known contacts (K1). Everyone can read;
 * only admins can change it, because turning it on is a privacy decision.
 */
export function BrowseTrackingCard() {
  const { isAdmin } = useAuth();
  const queryClient = useQueryClient();
  const settings = useQuery({
    queryKey: ['tracking-settings'],
    queryFn: () => apiFetch<TrackingSettings>('/settings/tracking'),
  });
  const save = useMutation({
    mutationFn: (input: { enabled: boolean; lowStockThreshold?: number }) =>
      apiFetch<TrackingSettings>('/settings/tracking', { method: 'PUT', body: input }),
    onSuccess: (saved) => queryClient.setQueryData(['tracking-settings'], saved),
  });

  const [threshold, setThreshold] = useState('5');
  const saved = settings.data?.lowStockThreshold;
  useEffect(() => {
    if (saved !== undefined) setThreshold(String(saved));
  }, [saved]);

  if (settings.isError) return <Alert>The browse tracking settings could not be loaded.</Alert>;
  if (!settings.data) return null;
  const { enabled, embedCode } = settings.data;

  return (
    <Card className="p-4 flex flex-col gap-3 max-w-[640px]">
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-col">
          <span className="text-sm font-semibold">Browse tracking is on</span>
          <span className="text-xs text-muted">
            Records which products a known contact looks at, so a browse abandonment flow can follow
            up. Off means nothing is recorded.
          </span>
        </div>
        <Switch
          label="Browse tracking is on"
          checked={enabled}
          disabled={!isAdmin || save.isPending}
          onChange={() => save.mutate({ enabled: !enabled })}
        />
      </div>
      <p className="m-0 text-xs text-muted">
        Only contacts who came from a newsletter link, or who are logged in to the shop, and who
        have accepted marketing or statistics cookies there. No IP address or browser details are
        stored, and the data is deleted after 30 days.
      </p>
      <div className="flex items-center gap-2">
        <label htmlFor="low-stock-threshold" className="text-sm">
          Low stock at
        </label>
        <input
          id="low-stock-threshold"
          type="number"
          min={1}
          max={1000}
          className="h-[34px] w-[80px] rounded-[9px] border border-zinc-300 bg-card px-2 text-sm"
          value={threshold}
          disabled={!isAdmin || save.isPending}
          onChange={(e) => setThreshold(e.target.value)}
          onBlur={() => {
            const n = Number(threshold);
            if (Number.isInteger(n) && n >= 1 && n <= 1000 && n !== saved) {
              save.mutate({ enabled, lowStockThreshold: n });
            } else {
              setThreshold(String(saved ?? 5));
            }
          }}
        />
        <span className="text-xs text-muted">
          items or fewer: a low-stock flow can then mail contacts who looked at the product or have
          it in their cart.
        </span>
      </div>
      {!isAdmin ? <p className="m-0 text-xs text-muted">Only an admin can change this.</p> : null}
      {save.isError ? <Alert>Could not be saved. Try again.</Alert> : null}
      {enabled && embedCode ? (
        <code className="block break-all rounded-[9px] bg-zinc-100 p-2 text-xs">{embedCode}</code>
      ) : null}
    </Card>
  );
}
